import { useEffect, useState } from "react";
import app from "Assets/Images/app.png";
import Footer from "components/Footer";
import { useNavigate } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi";
import ContentNavbar from "components/ContentNavbar";
import WelcomePageSidebar from "pages/welcome/WelcomePageSidebar";
import { contentNavData } from "constant/ContentNavBarData";
import { AppsData } from "constant/AppsData";
import GeneralServices from "services/General/GeneralServices";
import { Helmet } from "react-helmet-async";

interface AppLink {
  title: string;
  value: string;
}

function AppPage({ handleClick, isActive, setIsActive }: any) {
  const navigate = useNavigate();
  const [apps, setApps] = useState<AppLink[]>([]);
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;

  useEffect(() => {
    GeneralServices.getAllConfigData()
      .then((res) => {
        const filteredData = res.data.filter((item: AppLink) =>
          AppsData.some((a) => a.title === item.title && item.value !== null)
        );
        setApps(filteredData);
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>AYOZAT APPS</title>
        <meta
          name="description"
          content="Stream Movies, Series, Live TV, Music, Radio & Podcasts plus Live Pay-Per-View Events. Watch on AYOZAT STREAM. Your Entertainment Hub!"
        />
        <link rel="canonical" href={`${FE_URL}/apps`} />
        <meta
          property="og:title"
          content="Watch AYOZAT – Streaming Movies, Series, Live TV, Music, Radio & Podcasts plus Live Pay-Per-View Events."
        />
        <meta
          property="og:description"
          content="Stream Movies, Series, Live TV, Music, Radio & Podcasts plus Live Pay-Per-View Events. Watch on AYOZAT STREAM. Your Entertainment Hub!"
        />
        <meta property="og:image" content="https://ayozat.co.uk/logo192.png" />
        <meta property="og:type" content="website" />
        <meta name="og:url" content={`${FE_URL}/apps`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Watch AYOZAT – Streaming Movies, Series, Live TV, Music, Radio & Podcasts plus Live Pay-Per-View Events."
        />
        <meta
          name="twitter:description"
          content="Stream Movies, Series, Live TV, Music, Radio & Podcasts plus Live Pay-Per-View Events. Watch on AYOZAT STREAM. Your Entertainment Hub!"
        />
        <meta name="twitter:image" content="https://ayozat.co.uk/logo192.png" />
        <meta name="twitter:site" content="AYOZAT STREAM" />
        <meta name="twitter:url" content={`${FE_URL}/apps`} />
      </Helmet>

      <ContentNavbar handleClick={handleClick} />
      <div className="mt-[-10px]">
        <WelcomePageSidebar
          isActive={isActive}
          navData={contentNavData}
          className=""
          setIsActive={setIsActive}
        />
      </div>

      <div
        className={`${
          isActive ? "pl-[80px]" : "pl-0"
        } duration-500 max-sm:w-full`}
      >
        <div className="relative min-h-screen overflow-hidden text-white">
          {/* ambient glow backdrop (pure CSS, no external image) */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-40 right-[-10%] h-[900px] w-[900px] rounded-full opacity-40 blur-3xl"
            style={{
              background:
                "radial-gradient(circle at center, #f30543 0%, rgba(243,5,67,0.25) 40%, transparent 70%)",
            }}
          />

          {/* back button */}
          <button
            type="button"
            aria-label="Go back"
            onClick={() => navigate(-1)}
            className="relative z-20 ml-6 mt-8 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/20"
          >
            <HiArrowLeft />
          </button>

          {/* hero */}
          <div className="relative z-10 mx-auto flex max-w-6xl flex-col-reverse items-center gap-12 px-6 py-16 lg:flex-row lg:gap-8 lg:py-24">
            {/* left: copy + download buttons */}
            <div className="flex w-full flex-col items-center text-center lg:w-1/2 lg:items-start lg:text-left">
              <h1 className="text-4xl font-extrabold leading-tight max-sm:text-3xl">
                Take AYOZAT{" "}
                <span className="text-[#f30543]">everywhere</span>
              </h1>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-zinc-400">
                Over 100+ TV channels anytime, anywhere. Video on Demand
                anytime, anywhere. Watch live sports, entertainment, music,
                gaming, esports, kids and documentaries for free!
              </p>

              <div className="mt-10 flex w-full max-w-sm flex-col gap-3">
                {apps.map((link) => {
                  const appData = AppsData.find((a) => a.title === link.title);
                  if (!appData) return null;

                  const IconComponent = appData.icon;
                  return (
                    <a
                      key={link.title}
                      href={link.value}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-3 backdrop-blur transition-all duration-200 hover:border-[#f30543]/60 hover:bg-[#f30543]/10"
                    >
                      <IconComponent className="h-9 w-9 shrink-0" />
                      <div className="text-left">
                        <p className="text-[11px] uppercase tracking-wide text-zinc-400">
                          Download on
                        </p>
                        <p className="text-sm font-semibold uppercase text-white group-hover:text-[#f30543]">
                          {link.title.replace("_", " ")}
                        </p>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* right: app preview */}
            <div className="w-full max-w-md lg:w-1/2 lg:max-w-none">
              <img
                src={app}
                alt="AYOZAT app preview"
                className="mx-auto w-full max-w-lg drop-shadow-2xl"
              />
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </>
  );
}

export default AppPage;